import * as THREE from 'three';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader';

export class MaterialPreviewRendererInstance 
{
	private scene!: THREE.Scene;
	private camera!: THREE.PerspectiveCamera;
	private renderer!: THREE.WebGLRenderer;
	private sphere!: THREE.Mesh;
	private plane!: THREE.Mesh;
	private light!: THREE.DirectionalLight;
	private isInitialized: boolean = false;
	private initializing: boolean = false;
	private canvas?: HTMLCanvasElement;
	private forcedMaterial?: THREE.Material;
	private HDRLoaded: boolean = false;

	public updateCanvas(canvas: HTMLCanvasElement)
	{
		if (!this.isInitialized) return;
		this.canvas = canvas;
		this.setRenderer(new THREE.WebGLRenderer({ antialias: true, alpha: true, canvas: this.canvas }));
	}

	private async setRenderer(renderer: THREE.WebGLRenderer)
	{
		if (this.renderer)
		{
			this.cleanupRenderer();
		}
		this.renderer = renderer;
		if (!this.canvas)
		{
			this.renderer.setSize(256, 256);
		}
		else 
		{
			const rect = this.canvas.getBoundingClientRect();
			this.renderer.setSize(rect.width, rect.height);
		}
		this.renderer.setPixelRatio(window.devicePixelRatio);
		this.renderer.outputColorSpace = THREE.SRGBColorSpace;
		this.renderer.shadowMap.enabled = true;
		this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
		this.HDRLoaded = false;
		await this.loadHDRMap();
		this.light.shadow.needsUpdate = true;
		if (!this.cleaningUp)
		{
			this.renderScene()
		}
	}

	public setMaterial(material: THREE.Material)
	{
		this.forcedMaterial = material;
	}
	constructor(canvas?: HTMLCanvasElement, material?: THREE.Material)
	{
		this.canvas = canvas;
	}

	private async loadHDRMap(): Promise<void> 
	{
		return new Promise<void>((resolve) => 
		{
			new RGBELoader()
				.load('HDR/CreateStudio.hdr', (texture) => 
				{
					if (this.cleaningUp) return;
					texture.mapping = THREE.EquirectangularReflectionMapping;
					texture.colorSpace = THREE.SRGBColorSpace;

					const pmremGenerator = new THREE.PMREMGenerator(this.renderer);
					const envMap = pmremGenerator.fromEquirectangular(texture).texture;

					this.scene.environment = envMap;
					this.scene.environmentIntensity = 0.75;

					texture.dispose();
					pmremGenerator.dispose();
					
					this.HDRLoaded = true;
					resolve();
				});
		});
	}

	public async renderScene() 
	{
		if (!this.canvas || !this.HDRLoaded) 
		{
			return;
		}
		if (this.forcedMaterial)
		{
			this.sphere.material = this.forcedMaterial;
		}
		this.camera.aspect = this.renderer.domElement.width / this.renderer.domElement.height;
		this.camera.updateProjectionMatrix();
		this.renderer.render(this.scene, this.camera);
	}

	private async initialize(): Promise<void> 
	{
		this.scene = new THREE.Scene();

		this.camera = new THREE.PerspectiveCamera(
			25,
			1,
			0.1,
			1000
		);
		this.camera.position.set(4, 4, 4);
		this.camera.lookAt(0, 0, 0);

		


		const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
		directionalLight.position.set(-1, 5, 1);
		directionalLight.lookAt(0, 0, 0);
		directionalLight.castShadow = true;
		directionalLight.shadow.mapSize.set(256, 256);
		directionalLight.shadow.radius = 2;
		directionalLight.shadow.autoUpdate = false;
		this.light = directionalLight;
		this.scene.add(directionalLight);
		
		const planeGeometry = new THREE.PlaneGeometry(100, 100);
		const planeMaterial = new THREE.MeshStandardMaterial({
			color: 0xffffff,
			roughness: 0.8,
			metalness: 0.2,
		});
		this.plane = new THREE.Mesh(planeGeometry, planeMaterial);
		this.plane.rotation.x = -Math.PI / 2;
		this.plane.position.y = -1;
		this.plane.receiveShadow = true;
		this.scene.add(this.plane);

		const sphereGeometry = new THREE.SphereGeometry(1, 32, 32);
		const uvArray = sphereGeometry.attributes.uv.array;
		const newUvArray = new Float32Array(uvArray.length);
		for (let i = 0; i < uvArray.length; i++) 
		{
			if (i % 2 === 0) 
			{
				newUvArray[i] = uvArray[i];
			}
			else 
			{
				newUvArray[i] = 1 - uvArray[i];
			}
		}
		sphereGeometry.setAttribute('uv', new THREE.BufferAttribute(newUvArray, 2));
		
		const sphereMaterial = new THREE.MeshStandardMaterial({
			color: 0xffffff,
			roughness: 0.5,
			metalness: 0.5,
		});
		this.sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
		this.sphere.castShadow = true;
		this.scene.add(this.sphere);

		if (this.forcedMaterial)
		{
			this.sphere.material = this.forcedMaterial;
		}

		this.updateCheckerboardTexture();

		directionalLight.shadow.needsUpdate = true;

		this.sphere.onAfterRender = function (renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera, geometry: THREE.BufferGeometry, material: THREE.Material, group: any) 
		{
			if ("requiresMultipassRendering" in material) 
			{
				if ("multipassMaterials" in material) 
				{
					for (const multiPassMaterial of material.multipassMaterials as THREE.Material[]) 
					{
						if ("onMultipassRender" in multiPassMaterial) 
						{
							(multiPassMaterial as any).onMultipassRender(camera, scene, geometry, renderer, this, group);
						}
						else 
						{
							renderer.renderBufferDirect(camera, scene, geometry, multiPassMaterial, this, group);
						}
					}
				}
			}
		};

		const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, canvas: this.canvas });
		await this.setRenderer(renderer);
			
		this.isInitialized = true;
	}

	private updateCheckerboardTexture(): void 
	{
		const textureSize = 512;
		const canvas = document.createElement('canvas');
		canvas.width = textureSize;
		canvas.height = textureSize;
		const context = canvas.getContext('2d');
		
		if (context) 
		{
			const tileSize = textureSize / 32;
			
			for (let y = 0; y < 32; y++) 
			{
				for (let x = 0; x < 32; x++) 
				{
					context.fillStyle = (x + y) % 2 === 0 ? '#ffffff' : '#cccccc';
					context.fillRect(x * tileSize, y * tileSize, tileSize, tileSize);
				}
			}
			
			const texture = new THREE.CanvasTexture(canvas);
			texture.wrapS = THREE.RepeatWrapping;
			texture.wrapT = THREE.RepeatWrapping;
			texture.repeat.set(4, 4);
			
			(this.plane.material as THREE.MeshStandardMaterial).map = texture;
			(this.plane.material as THREE.MeshStandardMaterial).needsUpdate = true;
		}
	}

	private cleaningUp = false;

	private cleanupRenderer(): void
	{
		if (this.renderer?.domElement.parentNode) 
		{
			this.renderer.domElement.parentNode.removeChild(this.renderer.domElement);
		}
		
		this.renderer?.dispose();
		this.renderer?.forceContextLoss();
	}

	public cleanup(): void 
	{
		if (this.cleaningUp) return;
		this.cleaningUp = true;
		this.sphere?.geometry.dispose();
		this.plane?.geometry.dispose();
		
		if (this.plane?.material instanceof THREE.Material) 
		{
			this.plane?.material.dispose();
		}
		else 
		{
			this.plane?.material.forEach(material => material.dispose());
		}

		this.cleanupRenderer();

		this.isInitialized = false;
	}

	private async initializationFinished(): Promise<void> 
	{
		return new Promise<void>((resolve) => 
		{
			if (!this.initializing && this.isInitialized) 
			{
				resolve();
				return;
			}
			
			const checkInterval = setInterval(() => 
			{
				if (!this.initializing && this.isInitialized) 
				{
					clearInterval(checkInterval);
					resolve();
				}
			}, 100);
			
			setTimeout(() => 
			{
				clearInterval(checkInterval);
				if (!this.initializing && this.isInitialized) 
				{
					resolve();
				}
				else 
				{
					console.warn("[MaterialPreviewRenderer] Initialization timeout");
					resolve();
				}
			}, 5000);
		});
	}

	public generatePreview(material: THREE.Material): Promise<Blob> 
	{
		const renderPromise = new Promise<Blob>((resolve) => 
		{
			this.sphere.material = material;
			this.renderer.render(this.scene, this.camera);
			
			this.renderer.domElement.toBlob((blob) => 
			{
				if (blob) 
				{
					resolve(blob);
				}
			}, 'image/png');
		});

		return renderPromise;
	}

	public async initializeIfNeeded(): Promise<void> 
	{
		if (!this.isInitialized && !this.initializing) 
		{
			this.initializing = true;
			await this.initialize();
			this.initializing = false;
		}
		await this.initializationFinished();
	}

	public getRenderer(): THREE.WebGLRenderer
	{
		return this.renderer;
	}
}

export class MaterialPreviewRenderer 
{
	private static previewRenderingInstance?: MaterialPreviewRendererInstance;
	private static renderQueue: Map<string, Promise<Blob>> = new Map();
	private static isRendering: boolean = false;
	private static renderTimeout: NodeJS.Timeout | null = null;
	private static renderedPreviews: Map<string, Blob> = new Map();

	private static getInstance(): MaterialPreviewRendererInstance 
	{
		if (!MaterialPreviewRenderer.previewRenderingInstance) 
		{
			MaterialPreviewRenderer.previewRenderingInstance = new MaterialPreviewRendererInstance();
		}
		return MaterialPreviewRenderer.previewRenderingInstance;
	}

	private static processQueue(): void 
	{
		if (this.isRendering || this.renderQueue.size === 0) 
		{
			return;
		}

		this.isRendering = true;
		
		const promises = Array.from(this.renderQueue.values());
		
		Promise.all(promises)
			.then(() => 
			{
				this.isRendering = false;
				this.renderQueue.clear();
				
				if (this.renderTimeout) 
				{
					clearTimeout(this.renderTimeout);
				}
				
				this.renderTimeout = setTimeout(() => 
				{
					this.cleanup();
				}, 5000);
			})
			.catch(error => 
			{
				console.error(`[MaterialPreviewRenderer] Error processing queue:`, error);
				this.isRendering = false;
			});
	}

	private static cleanup(): void 
	{
		if (this.renderTimeout) 
		{
			clearTimeout(this.renderTimeout);
			this.renderTimeout = null;
		}
		this.renderQueue.clear();
		this.isRendering = false;
		this.getInstance().cleanup();
		this.previewRenderingInstance = undefined;
	}

	public static async renderPreview(materialAssetId: string, material: THREE.Material | null): Promise<Blob | undefined> 
	{
		if (!material) return undefined;
		
		const existingPreview = MaterialPreviewRenderer.renderedPreviews.get(materialAssetId);
		if (existingPreview) 
		{
			return existingPreview;
		}

		await MaterialPreviewRenderer.getInstance().initializeIfNeeded();

		if (this.renderQueue.has(materialAssetId)) 
		{
			return this.renderQueue.get(materialAssetId);
		}

		const renderPromise = new Promise<Blob>((resolve) => 
		{
			MaterialPreviewRenderer.getInstance().generatePreview(material).then((blob) => 
			{
				MaterialPreviewRenderer.renderedPreviews.set(materialAssetId, blob);
				resolve(blob);
			});
		});
		this.renderQueue.set(materialAssetId, renderPromise);
		this.processQueue();
		
		return renderPromise;
	}

	public static clearPreview(materialAssetId: string): void 
	{
		MaterialPreviewRenderer.renderedPreviews.delete(materialAssetId);
	}
}

// Example usage:
// const material = new THREE.MeshStandardMaterial({
//   color: 0xff0000,
//   roughness: 0.5,
//   metalness: 0.5
// });

// MaterialPreviewRenderer.renderPreview(material)
//   .then(dataUrl => {
//     // Use the data URL (e.g., set as src of an img element)
//     const img = document.createElement('img');
//     img.src = dataUrl;
//     document.body.appendChild(img);
//   });
